import React, { useEffect, useState } from 'react'
import { useFormik, FormikProvider } from 'formik'
import styled from 'styled-components'
import toast from 'react-hot-toast'
import { useAccountCreation } from 'contexts/AccountCreation'
import { useLocalForage } from 'contexts/LocalForage'
import Fieldset from 'components/FormComponents/Fieldset'
import FieldsetItem from 'components/FormComponents/Fieldset/FieldsetItem'
import FieldMessage from 'src/components/FormComponents/FieldMessage'
import Label from 'components/FormComponents/Label'
import Input from 'components/FormComponents/Input/Input'
import Dropdown from 'components/FormComponents/Dropdown'
import NumberInput from 'components/FormComponents/Input/NumberInput'
import PostalCodeInput from 'components/FormComponents/Address/PostalCodeInput'
import ButtonCta from 'components/Button/ButtonCta'
import Checkbox from 'components/Checkbox'
import Separator from 'components/Separator'
import Toggle from 'components/FormComponents/Toggle'

import { GENDER_OPTIONS, FREQUENCY_OPTIONS } from 'utils/constants'
import { isEmpty, isUserObjValid } from 'utils/validators'
import { validationSchema, getInitialValues } from './formValidation'
import { emailSubscription, vusePlusOptInConsent, privacyPolicy, RegisterLabel } from './consents'
import PropTypes from 'prop-types'
import { getProvinceFromPostalCode } from '../../../utils/validators'

const ConsentContainer = styled.div`
  margin: 1rem 0;
`

const CreationForm = ({ showScanIcon }) => {
  const { user, setUser, nextStep, previousStep, formValidationError, setFormError, patronScanUser } =
    useAccountCreation()

  const { getStoredItem } = useLocalForage()
  const [agentProvince, setAgentProvince] = useState(null)
  useEffect(() => {
    const fetchAgentProvince = async () => {
      const fetchedAgentInfo = await getStoredItem('agentInformation')
      setAgentProvince(fetchedAgentInfo.agentProvinceActivation)
    }

    fetchAgentProvince()
  }, [])

  const handleSecondStepSubmit = async (values) => {
    const province = getProvinceFromPostalCode(values?.postalCode)
    console.log('postal code in values2', province, agentProvince)
    setUser({
      ...user,
      ...values,
      consent: true,
      emailSubscription: true,
      province: province || agentProvince,
      postalCode: values?.postalCode,
    })
    nextStep()
  }
  const formik = useFormik({
    initialValues: getInitialValues(user),
    validationSchema: validationSchema(agentProvince),
    onSubmit: () => handleSecondStepSubmit(values),
    validateOnChange: true,
    validateOnBlur: true,
  })
  const { errors, touched, values, handleChange, handleBlur, setFieldValue, isSubmitting, handleSubmit, validateForm } =
    formik

  const isElligble = values.isSmoker || values.isVaper
  const handleIsVaperChange = async (event) => {
    setFieldValue('vapeFrequency', '')
    handleChange(event)
    showScanIcon(values.isSmoker || event?.target?.checked)
  }

  const handleIsSmokerChange = async (event) => {
    setFieldValue('smokeFrequency', '')
    handleChange(event)
    showScanIcon(values.isVaper || event?.target?.checked)
  }
  const handleDobChange = async (event) => {
    setFormError({ dob: false })
    handleChange(event)
  }
  const handlePhoneNumberChange = async (event) => {
    setFormError({ phoneNumber: false })
    handleChange(event)
  }

  useEffect(() => {
    // Extra check to test if the user object is well formed
    if (!isUserObjValid(user)) {
      toast.error('An error occurred please try again.')
      previousStep()
    }
  }, [user])

  useEffect(() => {
    Object.keys(patronScanUser).map(async (key) => {
      if (patronScanUser[key].length > 0) await setFieldValue(key, patronScanUser[key] || '')
    })
    setTimeout(() => {
      validateForm()
    }, 0)
  }, [patronScanUser])

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <Fieldset>
          <Label subLabel="Check all that apply">Which products do you currently consume?</Label>

          <FieldsetItem xs="1-1" sm="1-3">
            <Checkbox
              label={'Cigarettes'}
              name="isSmoker"
              onChange={handleIsSmokerChange}
              checked={values.isSmoker}
              error={!isElligble && (touched.isSmoker || touched.isVaper)}
            />
          </FieldsetItem>

          <FieldsetItem xs="1-1" sm="2-3" opacity={+values.isSmoker}>
            <Dropdown
              name="smokeFrequency"
              value={values.smokeFrequency || ''}
              options={FREQUENCY_OPTIONS}
              onChange={handleChange}
              placeholder="Select frequency"
              meta={{
                error: errors.smokeFrequency,
                touched: touched.smokeFrequency,
              }}
              onBlur={handleBlur}
              full
            />
          </FieldsetItem>

          <FieldsetItem xs="1-1" sm="1-3">
            <Checkbox
              label={'Vaping device'}
              name="isVaper"
              onChange={handleIsVaperChange}
              checked={values.isVaper}
              error={!isElligble && (touched.isSmoker || touched.isVaper)}
            />
          </FieldsetItem>

          <FieldsetItem xs="1-1" sm="2-3" opacity={+values.isVaper}>
            <Dropdown
              name="vapeFrequency"
              value={values.vapeFrequency || ''}
              options={FREQUENCY_OPTIONS}
              onChange={handleChange}
              placeholder="Select frequency"
              meta={{
                error: errors.vapeFrequency,
                touched: touched.vapeFrequency,
              }}
              onBlur={handleBlur}
              full
            />
          </FieldsetItem>

          {!isElligble && (touched.isSmoker || touched.isVaper) && (
            <FieldsetItem xs="1-1">
              <FieldMessage big>Sorry, you are ineligible to proceed further</FieldMessage>
            </FieldsetItem>
          )}
        </Fieldset>

        {isElligble && (
          <>
            <Separator />

            <Fieldset hint="Enter name and last name as displayed on your government ID.">
              <FieldsetItem xs="1-1" sm="1-2">
                <Input
                  label="First name"
                  name="firstName"
                  value={values.firstName}
                  autoComplete="off"
                  onChange={handleChange}
                  placeholder="John"
                  meta={{
                    error: errors.firstName,
                    touched: touched.firstName,
                  }}
                  onBlur={handleBlur}
                />
              </FieldsetItem>
              <FieldsetItem xs="1-1" sm="1-2">
                <Input
                  label="Last name"
                  name="lastName"
                  value={values.lastName}
                  autoComplete="off"
                  onChange={handleChange}
                  placeholder="Doe"
                  meta={{
                    error: errors.lastName,
                    touched: touched.lastName,
                  }}
                  onBlur={handleBlur}
                />
              </FieldsetItem>
            </Fieldset>

            <FieldsetItem xs="1-1">
              <PostalCodeInput
                label="Residential Postal Code"
                name="postalCode"
                placeholder="Type your postal code ex. H2F 5D2"
                value={values.postalCode}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="off"
                meta={{
                  error: errors.postalCode,
                  touched: touched.postalCode,
                }}
              />
            </FieldsetItem>

            <Fieldset>
              <FieldsetItem xs="1-1" sm="1-2">
                <NumberInput
                  label="Date of birth"
                  name="dob"
                  value={values.dob}
                  onChange={handleDobChange}
                  numberFormat="####/##/##"
                  placeholder="YYYY/MM/DD"
                  autoComplete="off"
                  meta={{
                    error: errors.dob,
                    touched: touched.dob,
                  }}
                  onBlur={handleBlur}
                  error={formValidationError?.dob}
                />
              </FieldsetItem>

              <FieldsetItem xs="1-1" sm="1-2">
                <NumberInput
                  label="Phone number"
                  name="phoneNumber"
                  value={values.phoneNumber}
                  onChange={handlePhoneNumberChange}
                  numberFormat="(###) ###-####"
                  placeholder="(555) 555-5555"
                  autoComplete="off"
                  meta={{
                    error: errors.phoneNumber,
                    touched: touched.phoneNumber,
                  }}
                  onBlur={handleBlur}
                  error={formValidationError?.phoneNumber}
                  optional
                />
              </FieldsetItem>
            </Fieldset>

            <Fieldset>
              <FieldsetItem xs="1-1" sm="1-2">
                <Dropdown
                  label="Gender"
                  name="gender"
                  value={values.gender || ''}
                  options={GENDER_OPTIONS}
                  onChange={handleChange}
                  placeholder="Select Gender"
                  meta={{
                    error: errors.gender,
                    touched: touched.gender,
                  }}
                  onBlur={handleBlur}
                  full
                  optional
                />
              </FieldsetItem>
            </Fieldset>
            <Separator />
            <Fieldset>
              <FieldsetItem xs="1-1">
                <Toggle
                  label={vusePlusOptInConsent}
                  name="vusePlusOptIn"
                  value={values.vusePlusOptIn}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  size="large"
                  meta={{
                    error: errors.vusePlusOptIn,
                    touched: touched.vusePlusOptIn,
                  }}
                />
              </FieldsetItem>
            </Fieldset>
            <Separator />

            <ConsentContainer>
              {RegisterLabel}
              {emailSubscription}
              {/* {vusePlusOptInConsent} */}
              {privacyPolicy}
            </ConsentContainer>

            <Separator />

            <Fieldset>
              <FieldsetItem xs="1-1">
                <ButtonCta type="submit" disabled={!isEmpty(errors)} isLoading={isSubmitting}>
                  Register
                </ButtonCta>
              </FieldsetItem>
            </Fieldset>
          </>
        )}
      </form>
    </FormikProvider>
  )
}
CreationForm.propTypes = {
  showScanIcon: PropTypes.func,
}

export default CreationForm
